import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { Badge, EmptyBox, Pagination, QueryBoundary, Table, Td, Th } from '../components/ui';
import { atLeast } from '../lib/roles';
import { dateTime } from '../lib/format';

const LIMIT = 100;

export default function Audit() {
  const { user } = useAuth();
  const [action, setAction] = useState('');
  const [offset, setOffset] = useState(0);

  const allowed = atLeast(user?.role, 'operator');

  const q = useQuery({
    queryKey: ['audit', action, offset],
    queryFn: () => api.audit({ action: action || undefined, limit: LIMIT, offset }),
    enabled: allowed,
  });

  if (!allowed) {
    return (
      <>
        <PageHeader title="Audit" />
        <EmptyBox>The audit trail requires the operator role.</EmptyBox>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Audit"
        description="Every operator action, append-only. Filter by action (exact or 'prefix.*')."
      />
      <div className="mb-4">
        <input
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setOffset(0);
          }}
          placeholder="e.g. account.* or model.promote"
          className="rounded border border-edge bg-surface px-2 py-1.5 text-sm outline-none focus:border-slate-500"
        />
      </div>
      <QueryBoundary q={q} isEmpty={q.data?.items.length === 0}>
        {q.data && (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Time</Th>
                  <Th>Actor</Th>
                  <Th>Action</Th>
                  <Th>Entity</Th>
                  <Th>Detail</Th>
                </tr>
              </thead>
              <tbody>
                {q.data.items.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-800/30">
                    <Td className="whitespace-nowrap text-xs text-slate-400">{dateTime(a.created_at)}</Td>
                    <Td className="text-xs">{a.actor_email ?? (a.actor_user_id != null ? `user ${a.actor_user_id}` : 'system')}</Td>
                    <Td>
                      <Badge tone="info">{a.action}</Badge>
                    </Td>
                    <Td className="text-xs text-slate-400">
                      {a.entity_type ?? '—'}
                      {a.entity_id != null && ` #${a.entity_id}`}
                    </Td>
                    <Td className="text-slate-400">{a.detail ?? '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination total={q.data.total} limit={LIMIT} offset={offset} onChange={setOffset} />
          </>
        )}
      </QueryBoundary>
    </>
  );
}
